/**
 * Unified category constants for spots
 * Consolidates category icons, colors, and related utilities
 */

import {
  Camera,
  Utensils,
  Activity,
  Bus,
  Home,
  Car,
  Footprints,
  Bike,
  Train,
  type LucideIcon,
} from "lucide-react";
import type { TravelMode } from "@tabipla/shared";

// ==================== Spot Categories ====================

export const SPOT_CATEGORIES = [
  "sightseeing",
  "food",
  "activity",
  "transport",
  "accommodation",
] as const;
export type SpotCategory = (typeof SPOT_CATEGORIES)[number];

/**
 * Category icon components
 */
export const CATEGORY_ICON_COMPONENTS: Record<SpotCategory, LucideIcon> = {
  sightseeing: Camera,
  food: Utensils,
  activity: Activity,
  transport: Bus,
  accommodation: Home,
};

/**
 * Category colors - simple badge format
 * Use for badges, tags, and simple color needs
 */
export const CATEGORY_BADGE_COLORS: Record<SpotCategory, string> = {
  sightseeing: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  food: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  activity: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  transport: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  accommodation: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
};

/**
 * Category colors - detailed format with bg, border, text
 * Use for timeline cards and components needing fine-grained control
 */
export const CATEGORY_DETAILED_COLORS: Record<
  SpotCategory,
  { bg: string; border: string; text: string }
> = {
  sightseeing: {
    bg: "bg-blue-50 dark:bg-blue-950",
    border: "border-blue-400 dark:border-blue-600",
    text: "text-blue-700 dark:text-blue-300",
  },
  food: {
    bg: "bg-orange-50 dark:bg-orange-950",
    border: "border-orange-400 dark:border-orange-600",
    text: "text-orange-700 dark:text-orange-300",
  },
  activity: {
    bg: "bg-green-50 dark:bg-green-950",
    border: "border-green-400 dark:border-green-600",
    text: "text-green-700 dark:text-green-300",
  },
  transport: {
    bg: "bg-gray-50 dark:bg-gray-900",
    border: "border-gray-400 dark:border-gray-600",
    text: "text-gray-700 dark:text-gray-300",
  },
  accommodation: {
    bg: "bg-purple-50 dark:bg-purple-950",
    border: "border-purple-400 dark:border-purple-600",
    text: "text-purple-700 dark:text-purple-300",
  },
};

/**
 * Default colors for unknown categories
 */
export const DEFAULT_CATEGORY_COLORS = {
  badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  detailed: {
    bg: "bg-gray-50 dark:bg-gray-900",
    border: "border-gray-400 dark:border-gray-600",
    text: "text-gray-700 dark:text-gray-300",
  },
};

/**
 * Get badge colors for a category, with fallback
 */
export function getCategoryBadgeColors(category: string | undefined | null): string {
  if (!category) return DEFAULT_CATEGORY_COLORS.badge;
  return CATEGORY_BADGE_COLORS[category as SpotCategory] ?? DEFAULT_CATEGORY_COLORS.badge;
}

/**
 * Get detailed colors for a category, with fallback
 */
export function getCategoryDetailedColors(category: string | undefined | null): {
  bg: string;
  border: string;
  text: string;
} {
  if (!category) return DEFAULT_CATEGORY_COLORS.detailed;
  return CATEGORY_DETAILED_COLORS[category as SpotCategory] ?? DEFAULT_CATEGORY_COLORS.detailed;
}

/**
 * Get icon component for a category
 */
export function getCategoryIconComponent(category: string | undefined | null): LucideIcon | null {
  if (!category) return null;
  return CATEGORY_ICON_COMPONENTS[category as SpotCategory] ?? null;
}

// ==================== Travel Modes ====================

export const TRAVEL_MODES: TravelMode[] = ["driving", "walking", "cycling", "transit", "bus"];

/**
 * Travel mode icon components
 */
export const TRAVEL_MODE_ICON_COMPONENTS: Record<TravelMode, LucideIcon> = {
  driving: Car,
  walking: Footprints,
  cycling: Bike,
  transit: Train,
  bus: Bus,
};

/**
 * Travel mode colors
 */
export const TRAVEL_MODE_COLORS: Record<TravelMode, string> = {
  driving: "text-blue-600",
  walking: "text-green-600",
  cycling: "text-orange-600",
  transit: "text-purple-600",
  bus: "text-teal-600",
};

/**
 * Get travel mode icon component
 */
export function getTravelModeIconComponent(mode: TravelMode): LucideIcon {
  return TRAVEL_MODE_ICON_COMPONENTS[mode];
}

/**
 * Get travel mode color
 */
export function getTravelModeColor(mode: TravelMode): string {
  return TRAVEL_MODE_COLORS[mode];
}
